import React, { useState, useEffect, useContext, useRef } from 'react';
import { IProps } from './types';
import * as S from './styles';
import ComboBox from '@components/ComboBox';
import { useDispatch } from 'react-redux';
import actions from '@state/actions';
import { cleanup } from '@testing-library/react';
import { isConstructorDeclaration } from 'typescript';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import { IUserType } from '@state/auth/types';

const VehiclesMetadata: React.FC<IProps> = (props) => {
  const authContext = React.useContext(AuthContext);
  const isAdmin = authContext.userData && authContext.userData.profile && authContext.userData.profile.role === IUserType.ADMIN;
  const userData = authContext && authContext.userData?authContext.userData.profile:undefined;
  const [year, setYear] = useState<string | undefined>(undefined);
  const [make, setMake] = useState<string | undefined>(undefined);
  const [model, setModel] = useState<string | undefined>(undefined);
  const [trim, setTrim] = useState<string | undefined>(undefined);
  const [yearOptions, setYearOptions] = useState<string[]>([]);
  const [makeOptions, setMakeOptions] = useState<string[]>([]);
  const [modelOptions, setModelOptions] = useState<string[]>([]);
  const [trimOptions, setTrimOptions] = useState<string[]>([]);

  const dispatch = useDispatch();

  const fetchYears = () => {
    return new Promise((resolve, reject) => {
      dispatch(actions.brands.fetchYears(undefined,JSON.stringify(userData),(result, error) => {
        if (error) reject(error)
        else resolve(result)
      }))
    });
  }

  const fetchMakes = (year: string) => {
    return new Promise((resolve, reject) => {
      dispatch(actions.brands.fetchMakes(year, userData.email, userData?.role, (result, error) => {
        if (error) reject(error)
        else resolve(result)
      }))
    });
  }

  const fetchModels = (year: string, make: string) => {
    return new Promise((resolve, reject) => {
      dispatch(actions.brands.fetchModels(year, make,undefined, (result, error) => {
        if (error) reject(error)
        else resolve(result)
      }))
    });
  }

  const fetchTrims = (year: string, make: string, model: string) => {
    return new Promise((resolve, reject) => {
      dispatch(actions.brands.fetchTrims(year, make, model,undefined, (result, error) => {
        if (error) reject(error)
        else resolve(result)
      }))
    });
  }

  const addCustomMetaData = (year: number, make?: string, model?: string, trim?: string) => {
    return new Promise((resolve, reject) => {
      dispatch(actions.brands.addCustomMetaDataVehicles({year: year, make: make, model: model, trim: trim}, (result, error) => {
        if (error) reject(error)
        else resolve(result)
      }))
    });
  }

  useEffect(() => {
    if (props.vehicleMetadata) {
      const run = async () => {     
        try {
          const makes: any = await fetchMakes(props.vehicleMetadata.year); 
          if (makes) setMakeOptions(makes);
        
          const models: any = await fetchModels(props.vehicleMetadata.year, encodeURIComponent(props.vehicleMetadata.make)); 
          if (models) setModelOptions(models);
        
          const trims: any = await fetchTrims(props.vehicleMetadata.year, encodeURIComponent(props.vehicleMetadata.make), encodeURIComponent(props.vehicleMetadata.model)); 
          if (trims) setTrimOptions(trims);
        } catch(error) {
          console.error(error);
        }
      }
      run();

      setYear(props.vehicleMetadata.year);
      setMake(props.vehicleMetadata.make);
      setModel(props.vehicleMetadata.model);
      setTrim(props.vehicleMetadata.trim);
    }
}, [props.vehicleMetadata])

  useEffect(() => {
      const run = async () => {     
        try {
          const years: any = await fetchYears(); 
          if (years) setYearOptions(years);
        } catch(error) {
          console.error(error);
        }
      }
      run();
  }, [])

  useEffect(() => {
    props.onChange({year: year, make: make, model: model, trim: trim})
  }, [year, make, model, trim])

  const handleSelectOption = async(id: string, value: string) => {
    clean(id);
    if (id==="year") setYear(value);
    if (id==="make") setMake(value);
    if (id==="model") setModel(value);
    if (id==="trim") setTrim(value);

    try {
      if (id==="year") {
        const makes: any = await fetchMakes(value); 
        if (makes) setMakeOptions(makes);
        else setMakeOptions([]);
      }
      if (id==="make" && year) {
        const models: any = await fetchModels(year, encodeURIComponent(value)); 
        if (models) setModelOptions(models);
        else setModelOptions([]);
      }

      if (id==="model" && year && make) {
        const trims: any = await fetchTrims(year, encodeURIComponent(make), encodeURIComponent(value)); 
        if (trims) setTrimOptions(trims);
        else setTrimOptions([]);
      }
    } catch(error) {
      console.error(error);
    }
  }

  const clean = (id: string) => {
    if (id!=='trim') {
      setTrim("");
      if (id!=='model') setTrimOptions([])
    }
    if (id==='year'){
      setMake("");
      setModelOptions([])
    }
    if (id==='make' || id==='year') setModel("");  
  }

  const handleAddNewOption = async(id: string, value: string) => {
    let request: any = {year: undefined, make: undefined, mode: undefined, trim: undefined};
    if (id==="year"){
      request = {...request, year: parseInt(value)};
    } 
    if (id==="make") {
      request = {...request, year: parseInt(year!), make: value};
    }
    if (id==="model") {
      request = {...request, year: parseInt(year!), make: make, model: value};
    }
    if (id==="trim") {
      request = {year: parseInt(year!), make: make, model: model, trim: value};
    }

    let success = false;
    try {
      await addCustomMetaData(request.year,request.make,request.model,request.trim);
      success = true;
    } catch(error) {
      console.error(error);
    }

    if (success) {
      clean(id);
      if (id==="year"){
        setYear(value);
        let actualYearOptions = yearOptions;
        actualYearOptions.push(value);
        actualYearOptions.sort();
        setYearOptions(actualYearOptions);
      } 
      if (id==="make") {
        setMake(value);
        let actualMakeOptions = makeOptions;
        actualMakeOptions.push(value);
        actualMakeOptions.sort();
        setYearOptions(actualMakeOptions);
      }
      if (id==="model") {
        setModel(value);
        let actualModelOptions = modelOptions;
        actualModelOptions.push(value);
        actualModelOptions.sort();
        setYearOptions(actualModelOptions);
      }
      if (id==="trim") {
        let actualTrimOptions = trimOptions;
        actualTrimOptions.push(value);
        actualTrimOptions.sort();
        setYearOptions(actualTrimOptions);
        setTrim(value);
      }
    }
  }

  const comboDisabled = (title: string): boolean => {
    let disabled = false;
    switch(title.toLowerCase()) {
      case "make":
        if (!year) disabled = true;
        break;
      case "model":
        if (!make) disabled = true;
        break;
      case "trim":
        if (!model) disabled = true;
        break;
    }
    return disabled;
  }

  const metaComboBox = (value: string | undefined, title: string, options: string[]) => {
    return (
      <ComboBox
        id={title.toLowerCase()}
        placeholder={`Select ${title}`}
        title={title}
        options={options}
        required={false}
        disabled={comboDisabled(title)}
        position={"bottom"}
        chooseOption={handleSelectOption}
        addNewOption={handleAddNewOption}
        value={value}
        containerStyle={{
          padding: "0px",
          width: "250px",
        }}
        titleStyle={{
          fontFamily: "Roboto",
          fontSize: "16px",
          fontWeight: 500,
          fontStretch: "normal",
          fontStyle: "normal",
          lineHeight: "normal",
          letterSpacing: "normal",
          textAlign: "left",
          color: comboDisabled(title)?"#C0C0C0":"#000000",
          margin: "0px 0px 7px 0px",
        }}
      />
    )
  }

  return (
    <S.Root>
      <S.Row>
        {metaComboBox(year, "Year", yearOptions)}
        {metaComboBox(make, "Make", makeOptions)}
      </S.Row>
      <S.Row>
        {metaComboBox(model, "Model", modelOptions)}
        {metaComboBox(trim, "Trim", trimOptions)}
      </S.Row>
    </S.Root>
  );
}

export default VehiclesMetadata;